/* eslint-disable no-unused-vars */
/* eslint-disable no-unused-expressions */
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import ShowroomCBBService from 'trade-in-360/services/ShowroomCBBService/ShowroomCBBService.js';
import TradeInService from 'trade-in-360/services/TradeInService/TradeInService.js';
import TradeInModelNew from 'trade-in-360/model/TradeInModelNew.js';
import TradeInCta from 'trade-in-360/components/TradeInCta/TradeInCta.jsx';
import REGEX from 'trade-in-360/constants/regex.js';
import WIDGET_VERSION from 'trade-in-360/constants/widgetVersions.js';
import WEB_CSS_CLASSNAMES from 'trade-in-360/constants/webCssClassnames.js';
import TradeInVehicleService from 'trade-in-360/services/TradeInVehicleService/TradeInVehicleService.js';
import 'trade-in-360/components/InstantEstimateWrapper/Steps/ContactInformations/ContactInformations.scss';
import { isEmpty, upperFirst, lowerCase } from 'lodash';
import { LocaleService, EmailService, ConfigsService, WindowProxy } from '@sm360/widget-commons-core-services';
import { ValidationService as VS } from '@sm360/widget-commons-forms';
import {
    AccountService,
    ProfileService,
    EVENTS,
    ShiftDigitalService,
    AnalyticsService,
    VIRTUAL_PAGEVIEWS as PAGES,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { Vehicle, TradeInsService } from '@sm360/widget-commons-vehicle';
import { Input, InputPhone, Checkbox, Card, CardSection, useDebounce, Select } from '@sm360/ui-library';
import ConfirmationModal from 'trade-in-360/components/ConfirmationModal/ConfirmationModal.jsx';
import ErrorMessage from 'trade-in-360/components/ErrorMessage/ErrorMessage.jsx';
import DealerLocationListingService from 'trade-in-360/services/DealerLocationListService/DealerLocationListService.js';
import { getLocaleFormat, allSettled } from '../../Utils/utils.js';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import ConfirmationMessage from '../../Components/ConfirmationMessage/ConfirmationMessage.jsx';
import PrivacyPolicy from '../../Components/PrivacyPolicy/PrivacyPolicy.jsx';

// Dealer name to be exclude from the dealer list dropdown
const excludeDealers = ['Mississauga Harley-Davidson'];

const ContactInformations = ({
    tradeInVehicle,
    onPreviousStep,
    onNextStep,
    onTradeInVehicleUpdated,
    onFormSubmit,
    inspectionNeeded,
    errorInspection: { questionError, answerError },
    closeModal,
    quote,
    previousLabel,
    isPhoneRequired,
    displayPolicyLabel,
    accountTradeInVehicle,
    config,
    isMultiDealerTradeIn,
    websiteDomain,
    sessionUserGeoData,
    standAlone,
}) => {
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [email, setEmail] = useState('');
    const [mobilePhone, setPhone] = useState('');
    const [optin, setOptin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [leadCreated, setLeadCreated] = useState(false);
    const [error, setError] = useState('');
    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const initialMount = useRef(true);
    const [selectedOrgUnitId, setDealerOrgUnitId] = useState(0);
    const [dealerLocationList, setDealerLocationList] = useState([]);
    const [startInput, setStartInput] = useState(false);
    const {
        fieldsLocked,
        version,
        skipEstimate,
        useCBB,
        isViewOnly,
        isNewRequest,
        setNewRequest,
        officialEdit,
        vehicleInventoryId,
        catalogVehicleId,
    } = useContext(NewTradeInContext);
    const isTrade360 = version === WIDGET_VERSION.TRADE_360;

    // On mount
    useEffect(() => {
        const fetchProfile = async () => {
            const account = await ProfileService.fetchProfile();
            if (account) {
                account?.firstname && setFirstname(account.firstname);
                account?.lastname && setLastname(account.lastname);
                account?.username && setEmail(account.username);
                if (account?.mobilePhone) {
                    // Need to trim +1 from phone if available since the phone field is considering +1 as two digits out of 10digits required
                    account.mobilePhone.indexOf('+1') === 0
                        ? setPhone(account.mobilePhone.replace(/^\+[0-9]/, ''))
                        : setPhone(account.mobilePhone);
                }
                account?.optInEmail && setOptin(account.optInEmail);
            } else throw new Error('Account is not defined');
        };

        // Get contact data from props 'tradeInVehicle'
        if (tradeInVehicle?.contact) {
            const { contact } = tradeInVehicle;
            contact?.firstname && setFirstname(contact.firstname);
            contact?.lastname && setLastname(contact.lastname);
            contact?.email && setEmail(contact.email);
            contact?.mobilePhone && setPhone(contact.mobilePhone);
            contact?.optin && setOptin(contact.optin);
        }
        // Get info from logged account
        else if (AccountService.isAuthenticated()) {
            try {
                fetchProfile();
            } catch (e) {
                this.logger.error('Error fetching the profile information', e);
            }
        }
        AnalyticsService.virtualPageView(PAGES.ESTIMATED_TRADEIN_CONTACT_INFO());
    }, []);
    useEffect(() => {
        // To get province Id by from province code
        const getIdByProvinceCode = (provinceCode, dealerData) => {
            const { provincesById } = dealerData || {};
            const provincesArray = Object.values(provincesById);

            // Loop through the province keys to find the matching province code
            const matchingProvince = provincesArray.find(
                (province) => province?.provinceCode?.toLowerCase() === provinceCode?.toLowerCase()
            );

            // If a matching province is found, return its ID
            if (matchingProvince) {
                return matchingProvince.id;
            }
            // If the province code is not found, return null or any other suitable value
            return null;
        };

        // Refined dealer list based on nearest distance first
        const getRefinedDealersList = (dealerData) => {
            return dealerData.sort((a, b) => a.distance - b.distance) || [];
        };

        // Format Dealer list for dropdown
        const formatDealerList = (dealerList) => {
            return dealerList
                .filter(({ name }) => !excludeDealers.includes(name))
                .map(({ id, name, address, city, region, postalCode }) => ({
                    value: parseInt(id, 10),
                    label: `${name}\n${address}, ${city}, ${region}, ${postalCode}`,
                }));
        };

        // Fetch dealerlist for dropdown
        const fetchDealerList = async () => {
            const provinceCodeToFind = config.location || 'ON';
            const { sessionUserLatitude, sessionUserLongitude } = sessionUserGeoData || {};
            // Dealer location raw data
            const dealerData = await DealerLocationListingService.fetchTradeInDealerList(
                provinceCodeToFind,
                config?.orgId,
                sessionUserLatitude,
                sessionUserLongitude
            );
            // Get province Id from the raw data
            const provinceId = getIdByProvinceCode(provinceCodeToFind, dealerData);

            let dealerList = [];
            if (sessionUserLatitude && sessionUserLongitude) {
                dealerList = getRefinedDealersList(dealerData?.organizationUnits);
            } else if (!sessionUserLatitude && !sessionUserLongitude && provinceId) {
                dealerList = dealerData?.organizationUnitsByProvince[provinceId];
            }
            if (dealerList?.length > 0) {
                const dealerLocation = formatDealerList(dealerList);
                setDealerLocationList(dealerLocation);
            }
        };
        if (isMultiDealerTradeIn) {
            try {
                fetchDealerList();
            } catch (e) {
                this.logger.error('Error fetching the Dealer location list', e);
            }
        }
    }, []);

    const memoizedTradeInVehicle = useMemo(
        () => ({
            ...tradeInVehicle,
            contact: {
                firstname,
                lastname,
                email,
                mobilePhone,
                optin,
            },
        }),
        [firstname, lastname, email, mobilePhone, optin]
    );
    const debouncedTradeInVehicle = useDebounce(memoizedTradeInVehicle, 300);

    useEffect(
        () => {
            if (initialMount.current) {
                initialMount.current = false;
            } else if (!fieldsLocked) onTradeInVehicleUpdated(debouncedTradeInVehicle);
        },
        [debouncedTradeInVehicle]
    );

    const isFormValid = () => {
        const validatePhone = isPhoneRequired ? VS.validatePhoneNumber : VS.optional(VS.validatePhoneNumber);
        const validateEmail = (v) => REGEX.NOT_CONTAINS_TEST.test(v) && VS.validateEmail(v);
        const validateDealerSelection = (orgUnitId) => {
            if (isMultiDealerTradeIn && dealerLocationList.length > 0) return orgUnitId > 0;
            return true;
        };
        // workaround for autofill
        const phoneInput = document.querySelector('#phone');
        const isPhoneInputValid =
            phoneInput?.value && phoneInput?.validity?.valid && phoneInput?.getAttribute('aria-invalid') === 'false';
        return (
            firstname.length > 0 &&
            lastname.length > 0 &&
            validateEmail(email) &&
            (validatePhone(String(mobilePhone)) || isPhoneInputValid) &&
            validateDealerSelection(selectedOrgUnitId)
        );
    };

    const dispatchTradeInCreatedEvent = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_CREATED));
        }
    };

    const getEstimation = (estimation, amountWasUpdated = false) => {
        let updatedEstimation;
        if (isTrade360) {
            updatedEstimation = {
                amount: estimation?.amount || 0,
                amountWasUpdated,
                high: estimation?.tradeInExtraInfo?.estimation?.high || 0,
                low: estimation?.tradeInExtraInfo?.estimation?.low || 0,
            };
        } else {
            updatedEstimation = {
                amount: estimation?.amount || 0,
                amountWasUpdated,
                high: estimation?.amount || 0,
                low: estimation?.amount || 0,
            };
        }
        return updatedEstimation;
    };

    const createTradeInLead = (tradeInToSave) => {
        // Get the quote details from V2.5 VDP page
        const { quote: WebAutoCalculatorQuote } =
            (window.WebAutoCalculator && window.WebAutoCalculator.getInfo()) || {};

        const availableQuote = !isEmpty(WebAutoCalculatorQuote) ? WebAutoCalculatorQuote : quote;
        return TradeInService.saveTradeInNew(
            tradeInToSave,
            availableQuote,
            false,
            config,
            !AccountService.isAuthenticated(),
            vehicleInventoryId,
            catalogVehicleId,
            standAlone
        ).then((tradeInResponse) => {
            ShiftDigitalService.setDealerMake(config?.dealerMake);

            ShiftDigitalService.createFormEvent(
                tradeInResponse.leadId,
                'tradein',
                tradeInToSave?.tradeInVehicle?.contact?.optin,
                null,
                !!standAlone,
                quote?.vehicle,
                {
                    year: tradeInVehicle?.year,
                    make: tradeInVehicle?.make,
                    model: tradeInVehicle?.model,
                }
            );
            ShiftDigitalService.createTradeInFormSubmissionEvent(
                tradeInToSave?.tradeInVehicle?.year,
                tradeInToSave?.tradeInVehicle?.make,
                tradeInToSave?.tradeInVehicle?.model,
                tradeInResponse.leadId
            );
            if (useCBB) {
                // Trigger an event only for Instant estimate tradeIn
                ShiftDigitalService.updateDrTradeinFinish(availableQuote?.id, availableQuote?.vehicle);
            }
        });
    };

    const deleteExistingTradeIn = async () => {
        // Delete tradeIn only if tradeIn is available into account and its trade360 dealer site
        if (accountTradeInVehicle?.id && isTrade360) {
            const windowProxy = WindowProxy.get();
            try {
                const response = await TradeInsService.deleteTradeIn({ tradeId: accountTradeInVehicle?.id });
                if (response?.data?.id) {
                    if (windowProxy) {
                        windowProxy.localStorage.setItem('tradeIn', 0);
                    }
                    return Promise.resolve();
                }
            } catch (e) {
                // TODO: Better handle this error
                this.logger.error('Error deleting existing tradeIn', e);
            }
        }
        return Promise.resolve();
    };

    const setTradeInToSave = (tradeInToSave, tradeInVehicleData, amountWasUpdated, inspectionNeededMessage) => {
        let updatedTradeInToSave = tradeInToSave;
        if (tradeInVehicleData) {
            updatedTradeInToSave = updatedTradeInToSave.set('estimation', getEstimation(tradeInVehicleData, true));
        }
        if (inspectionNeededMessage) {
            updatedTradeInToSave = updatedTradeInToSave.set('info', inspectionNeededMessage);
        }
        return updatedTradeInToSave;
    };

    const sendTradeInEmail = (data) => {
        const amount = data?.estimation?.amount;
        const lowAmount = data?.estimation?.tradeInExtraInfo?.estimation?.low;
        const highAmount = data?.estimation?.tradeInExtraInfo?.estimation?.high;
        const hasValidAmounts = amount && lowAmount && highAmount;
        const amountPercentage = hasValidAmounts ? ((amount - lowAmount) * 100) / (highAmount - lowAmount) : 0;
        return EmailService.sendTradeInStandardEmail({
            websiteId: config?.websiteId || ConfigsService.getWebsiteId(),
            locale: LocaleService.getSimpleLocale(),
            clientName: `${data?.contact?.firstname} ${data?.contact?.lastname}`,
            year: data?.year,
            make: data?.make,
            model: data?.model,
            trim: data?.trim,
            vin: data?.vin,
            exteriorColor: data?.exteriorColor?.description,
            amount: getLocaleFormat(amount, LocaleService.getLocale()),
            toEmail: data?.contact?.email,
            conditionFormList: data?.formattedConditionReport,
            organizationUnitName: config?.orgUnitName || ConfigsService.getOrganizationUnitName() || '',
            organizationUnitPhone: config?.orgUnitPhone || ConfigsService.getOrganizationUnitPhone() || '',
            websiteDomain: config?.websiteDomain || ConfigsService.getWebsiteDomain() || '',
            websiteLogo: config?.websiteLogo || ConfigsService.getWebsiteLogo() || '',
            websiteFacebookUrl: config?.websiteFacebookUrl || ConfigsService.getWebsiteFacebookUrl() || '',
            websiteInstagramUrl: config?.websiteInstagramUrl || ConfigsService.getWebsiteInstagramUrl() || '',
            websiteTwitterUrl: config?.websiteTwitterUrl || ConfigsService.getWebsiteTwitterUrl() || '',
            websiteYoutubeUrl: config?.websiteYoutubeUrl || ConfigsService.getWebsiteYoutubeUrl() || '',
            lowAmount: getLocaleFormat(lowAmount, LocaleService.getLocale()),
            highAmount: getLocaleFormat(highAmount, LocaleService.getLocale()),
            amountPercentage: Math.round(amountPercentage).toString(),
            vehicleOptionsList: data?.selectedVehicleOptions,
            ...(vehicleInventoryId && { vehicleInventoryId }),
        });
    };

    // Store contact information in TradeInVehicle obj and send it to next screen
    const sendVehicleContactForm = (event) => {
        if (event?.preventDefault) event.preventDefault();
        if (isViewOnly || fieldsLocked) return onNextStep();
        // Validate form
        if (isFormValid()) {
            deleteExistingTradeIn().then(() => {
                const tradeVehicleToken = LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.TOKEN_LEAD', {
                    token: tradeInVehicle?.tradeVehicleToken,
                });

                let finalMobilePhone = mobilePhone;

                // workaround for autofill
                if (isPhoneRequired && !mobilePhone) {
                    const phoneInput = document.querySelector('#phone');
                    finalMobilePhone = phoneInput?.value?.replace(/[^a-zA-Z0-9]/g, '');
                }

                const updatedTradeInVehicle = {
                    contact: {
                        firstname,
                        lastname,
                        email,
                        mobilePhone: finalMobilePhone,
                        optin,
                    },
                    details: useCBB
                        ? TradeInVehicleService.getVehicleOptionsList(tradeInVehicle) + tradeVehicleToken
                        : '',
                };
                const data = { ...tradeInVehicle, ...updatedTradeInVehicle };

                const tradeInToSave = new TradeInModelNew({
                    id: '',
                    leadId: '',
                    tradeInVehicle: data,
                    profile: updatedTradeInVehicle.contact,
                    evaluationQuestion: officialEdit ? data.estimation.evaluationQuestion : {},
                });

                setLoading(true);
                if (useCBB) {
                    ShowroomCBBService.getTradeInVehicleEstimation(tradeInVehicle.tradeVehicleToken)
                        .then((tradeInVehicleData) => {
                            if (!inspectionNeeded) data.estimation = tradeInVehicleData;

                            const inspectionNeededMessage = inspectionNeeded
                                ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.LEAD_INFO', {
                                      questionError,
                                      answerError,
                                  })
                                : '';

                            // tradeInToSave = tradeInToSave.set('estimation', getEstimation(tradeInVehicleData, true));
                            // tradeInToSave = tradeInToSave.set('info', inspectionNeededMessage);

                            const { quote: WebAutoCalculatorQuote } =
                                (window.WebAutoCalculator && window.WebAutoCalculator.getInfo()) || {};

                            const availableQuote = !isEmpty(WebAutoCalculatorQuote) ? WebAutoCalculatorQuote : quote;

                            allSettled([
                                createTradeInLead(
                                    setTradeInToSave(tradeInToSave, tradeInVehicleData, true, inspectionNeededMessage)
                                ),
                                inspectionNeeded // if Inspected needed: no need to send an email
                                    ? Promise.resolve('Inspected Needed: no email sent')
                                    : sendTradeInEmail(data),
                            ])
                                .then(() => {
                                    return AccountService.isAuthenticated()
                                        ? setTimeout(() => {
                                              TradeInService.saveTradeInNew(
                                                  setTradeInToSave(
                                                      tradeInToSave,
                                                      tradeInVehicleData,
                                                      false, // need amountUpdated false for all cases
                                                      inspectionNeededMessage
                                                  ),
                                                  availableQuote,
                                                  false,
                                                  config,
                                                  true
                                              );
                                          }, 3000)
                                        : Promise.resolve();
                                })
                                .then(() => {
                                    if (inspectionNeeded) return setLeadCreated(true);
                                    // Send a SD event on successfully get an appraisal
                                    // TODO: Should trigger a SD event for Dilawri version as well?
                                    // if (isTrade360) {
                                    //     ShiftDigitalService.updateDrTradeinFinish(quote?.id, quote?.vehicle);
                                    // }
                                    dispatchTradeInCreatedEvent();
                                    return onFormSubmit(data, selectedOrgUnitId || config.orgUnitId);
                                })
                                .catch((e) => {
                                    setError(LocaleService.t('TRADE_IN.ERROR_FETCHING'));
                                    return Promise.reject(e);
                                })
                                .finally(() => setLoading(false));
                        })
                        .catch((e) => {
                            setError(LocaleService.t('TRADE_IN.ERROR_FETCHING_ESTIMATION'));
                            setLoading(false);
                            return Promise.reject(e);
                        });
                } else {
                    createTradeInLead(setTradeInToSave(tradeInToSave))
                        .then(() => {
                            dispatchTradeInCreatedEvent();
                            setNewRequest(false);
                            return onFormSubmit(data, selectedOrgUnitId || config.orgUnitId);
                        })
                        .finally(() => setLoading(false));
                }
            });
        }
        return false;
    };

    const displayTitle = () => {
        return <h5>{LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}</h5>;
    };

    const displayTrade360Title = () => {
        return (
            <div className={`${WEB_CSS_CLASSNAMES.SUBHEADING1} margin-bottom-1`}>
                {LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO_TITLE')}
            </div>
        );
    };

    /**
     * For Trade360 version display trade-in confirmation modal if a tradeIn is already in the account
     */
    const onFormSubmitHandler = (event) => {
        if (event?.preventDefault) event.preventDefault();
        const fieldsDisabled = fieldsLocked || isViewOnly;
        if (isTrade360 && !officialEdit && (accountTradeInVehicle?.id && !fieldsDisabled))
            return setConfirmationModalOpen(true);
        // Normal flow for Dilawri version and/or no tradeIn in the account
        return sendVehicleContactForm();
    };

    const displayButtons = () => {
        let nextLabel = isTrade360
            ? LocaleService.t('TRADE_IN.CTA.GET_TRADE_ESTIMATE_VALUE')
            : LocaleService.t('TRADE_IN.CTA.GET_APPRAISAL');

        if (inspectionNeeded) nextLabel = LocaleService.t('TRADE_IN.BUTTONS.SUBMIT');

        return (
            <section
                className={`clearfix ${isTrade360 ? '' : 'vehicle-buttons-container'}`}
                data-test="trade-in.vehicle-form.submit"
            >
                <div className="vehicle-buttons">
                    {!inspectionNeeded && (
                        <ActionButton
                            onClick={onPreviousStep}
                            noPrefix={isTrade360}
                            noArrow={isTrade360}
                            className="btn-secondary"
                        >
                            {previousLabel || LocaleService.t('TRADE_IN.BUTTONS.BACK')}
                        </ActionButton>
                    )}
                    {useCBB && !skipEstimate && !officialEdit ? (
                        <ActionButton next noPrefix noArrow={isTrade360} loading={loading} className="btn-primary">
                            {nextLabel}
                        </ActionButton>
                    ) : (
                        <TradeInCta
                            officialAnswers={isNewRequest ? {} : accountTradeInVehicle?.evaluationQuestion}
                            onOfficialEvaluation={onFormSubmitHandler}
                            fetching={loading}
                            isMainCTA
                            className="non-cbb-cta"
                        />
                    )}
                </div>
            </section>
        );
    };

    const setPromotions = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_PROMOTIONS_CHECKBOX());
        setOptin();
    };

    const handleDealerLocationSelection = (value) => {
        setDealerOrgUnitId(value);
        ConfigsService.setOrganizationUnitId(value);
        config.orgUnitId = value;
    };

    const handleInputChange = () => {
        if (!startInput) {
            ShiftDigitalService.setDealerMake(config?.dealerMake);
            ShiftDigitalService.createTradeInFormStartEvent(
                tradeInVehicle?.year,
                tradeInVehicle?.make,
                tradeInVehicle?.model
            );
            setStartInput(true);
        }
    };

    const firstnameLabel = LocaleService.t('TRADE_IN.CONTACT.FIRST_NAME');
    const lastnameLabel = LocaleService.t('TRADE_IN.CONTACT.LAST_NAME');
    const phoneLabel = LocaleService.t(`TRADE_IN.CONTACT.PHONE${isTrade360 ? '' : '_NUMBER'}`);
    const emailLabel = LocaleService.t('TRADE_IN.CONTACT.EMAIL');

    return (
        <div className="contact-info-form">
            <div>
                {!isEmpty(error) && (
                    // Display an error box if lead creation or appraisal value request failed
                    <ErrorMessage message={error} />
                )}
                {inspectionNeeded ? (
                    <div className="contact-info-form--inspection-needed">
                        {leadCreated ? (
                            <ConfirmationMessage
                                title={LocaleService.t('TRADE_IN.THANK_YOU')}
                                description={LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.CONFIRMATION'
                                )}
                                closeText={LocaleService.t('TRADE_IN.BUTTONS.KEEP_BROWSING')}
                                onClose={closeModal}
                            />
                        ) : (
                            <>
                                <div className="contact-info-form--inspection-needed-header">
                                    <icon className="icon-cbb-icons-inspection" />
                                    <h3 className="contact-info-form--inspection-needed-header--title">
                                        {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.TITLE')}
                                    </h3>
                                </div>
                                <p className="contact-info-form--inspection-needed--desc">
                                    {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.INSPECTION_NEEDED.DESCRIPTION')}
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    !isTrade360 && displayTitle()
                )}
                <div className="contact-info-form-body">
                    {!leadCreated && (
                        <>
                            {!isTrade360 && <p className="sr-text">{LocaleService.t('TRADE_IN.REQUIRED_FIELDS')}</p>}
                            <form className="trade-in-form" onSubmit={onFormSubmitHandler}>
                                <div className="step-section">
                                    {isTrade360 && displayTrade360Title()}
                                    <section className="clearfix contact-info-form-body--text-input">
                                        <div className="clearfix">
                                            <Input
                                                id="firstname"
                                                name="firstname"
                                                value={firstname}
                                                placeholder={
                                                    isTrade360
                                                        ? LocaleService.t('TRADE_IN.INPUT_PLACEHOLDER.FIRSTNAME')
                                                        : ''
                                                }
                                                onChange={(v) => {
                                                    setFirstname(v);
                                                    handleInputChange();
                                                }}
                                                label={
                                                    isTrade360 ? upperFirst(lowerCase(firstnameLabel)) : firstnameLabel
                                                }
                                                required
                                                disabled={fieldsLocked}
                                                customMessage={{
                                                    required: LocaleService.t('TRADE_IN.REQUIRED'),
                                                }}
                                                withErrorTooltip={false}
                                                icon="icon"
                                            />
                                        </div>
                                        <div className="clearfix">
                                            <Input
                                                id="lastname"
                                                name="lastname"
                                                label={
                                                    isTrade360 ? upperFirst(lowerCase(lastnameLabel)) : lastnameLabel
                                                }
                                                value={lastname}
                                                placeholder={
                                                    isTrade360
                                                        ? LocaleService.t('TRADE_IN.INPUT_PLACEHOLDER.LASTNAME')
                                                        : ''
                                                }
                                                onChange={(v) => {
                                                    setLastname(v);
                                                    handleInputChange();
                                                }}
                                                required
                                                disabled={fieldsLocked}
                                                customMessage={{
                                                    required: LocaleService.t('TRADE_IN.REQUIRED'),
                                                }}
                                                withErrorTooltip={false}
                                                icon="icon"
                                            />
                                        </div>
                                    </section>
                                    <section
                                        className={`clearfix contact-info-form-body--text-input ${
                                            isMultiDealerTradeIn ? 'isMultiDealerTradeIn' : ''
                                        }`}
                                    >
                                        <div className="clearfix">
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                value={email}
                                                placeholder={
                                                    isTrade360
                                                        ? LocaleService.t('TRADE_IN.INPUT_PLACEHOLDER.EMAIL')
                                                        : ''
                                                }
                                                onChange={(v) => {
                                                    setEmail(v);
                                                    handleInputChange();
                                                }}
                                                label={emailLabel}
                                                required
                                                disabled={fieldsLocked}
                                                customValidation={REGEX.NOT_CONTAINS_TEST}
                                                customMessage={{
                                                    required: LocaleService.t('TRADE_IN.REQUIRED'),
                                                    email: LocaleService.t('TRADE_IN.EMAIL.ERROR'),
                                                    customValidation: LocaleService.t(
                                                        'TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.NO_EMAIL_TEST'
                                                    ),
                                                }}
                                                withErrorTooltip={false}
                                                icon="icon"
                                            />
                                            {!inspectionNeeded &&
                                                !isTrade360 && (
                                                    <p>
                                                        {LocaleService.t(
                                                            'TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.SEND_EMAIL_COPY'
                                                        )}
                                                    </p>
                                                )}
                                        </div>
                                        <div className="clearfix">
                                            <InputPhone
                                                id="phone"
                                                className="input-phone"
                                                name="phone"
                                                type="tel"
                                                label={phoneLabel}
                                                value={mobilePhone}
                                                placeholder={
                                                    isTrade360
                                                        ? LocaleService.t('TRADE_IN.INPUT_PLACEHOLDER.PHONE')
                                                        : ''
                                                }
                                                onChange={(v) => {
                                                    setPhone(v ? v.toString() : '');
                                                    handleInputChange();
                                                }}
                                                disabled={fieldsLocked}
                                                required={isPhoneRequired}
                                                customMessage={{
                                                    required: LocaleService.t('TRADE_IN.REQUIRED'),
                                                    tel: LocaleService.t('TRADE_IN.PHONE.ERROR'),
                                                }}
                                                withErrorTooltip={false}
                                                icon="icon"
                                            />
                                        </div>
                                    </section>
                                    {isMultiDealerTradeIn &&
                                        dealerLocationList &&
                                        dealerLocationList?.length > 0 && (
                                            <section className="clearfix contact-info-form-body--dealer-select">
                                                <Select
                                                    required
                                                    label={LocaleService.t(
                                                        'TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.DEALER_SELECTION'
                                                    )}
                                                    id="dealer-selection"
                                                    options={dealerLocationList}
                                                    onChange={(v) => {
                                                        handleDealerLocationSelection(v);
                                                        handleInputChange();
                                                    }}
                                                    value={selectedOrgUnitId}
                                                    withErrorTooltip={false}
                                                    customMessage={{
                                                        required:
                                                            'In order to better serve please select your preferred dealer location',
                                                    }}
                                                />
                                            </section>
                                        )}
                                    <section className="clearfix contact-info-form-body--checkbox-input">
                                        <Checkbox
                                            checked={optin}
                                            onChange={(v) => {
                                                setPromotions(v);
                                                handleInputChange();
                                            }}
                                            label={LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.OPTIN')}
                                            disabled={fieldsLocked}
                                            className={isTrade360 ? 'fineprint smallprint' : ''}
                                        />
                                    </section>
                                    {displayPolicyLabel && (
                                        <section className="clearfix contact-info-form-body">
                                            <PrivacyPolicy />
                                        </section>
                                    )}
                                    {!isTrade360 && displayButtons()}
                                </div>
                                {isTrade360 && displayButtons()}
                            </form>
                        </>
                    )}
                </div>
                <ConfirmationModal
                    isOpen={isConfirmationModalOpen}
                    onCancel={() => setConfirmationModalOpen(false)}
                    onConfirm={() => {
                        setConfirmationModalOpen(false);
                        sendVehicleContactForm();
                    }}
                    title={LocaleService.t('TRADE_IN.CONFIRMATION_MODAL_NEW_TRADEIN.TITLE')}
                    description={LocaleService.t('TRADE_IN.CONFIRMATION_MODAL_NEW_TRADEIN.DESCRIPTION')}
                    confirmLabel={LocaleService.t('TRADE_IN.BUTTONS.CONFIRM')}
                    cancelLabel={LocaleService.t('TRADE_IN.BUTTONS.CANCEL')}
                    isMainCTA
                    isSingleVehicleConfirmation
                />
            </div>
        </div>
    );
};

ContactInformations.defaultProps = {
    config: {},
    isMultiDealerTradeIn: false,
    standAlone: false,
};

ContactInformations.propTypes = {
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    previousLabel: PropTypes.string.isRequired,
    config: PropTypes.object,
    isMultiDealerTradeIn: PropTypes.bool.isRequired,
    standAlone: PropTypes.bool.isRequired,
};

ContactInformations.displayName = 'ContactInformations';

function mapStateToProps(state) {
    return {
        accountTradeInVehicle: state.tradeInVehicle,
    };
}

export default connect(
    mapStateToProps,
    null
)(ContactInformations);
